import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface Operator {
  id: number;
  name: string;
  code: string;
}

interface PayoutMatrixCell {
  betType: string;
  multiplier?: number;
  isValid: boolean;
  isCustom: boolean;
}

interface PayoutMatrixRow {
  prizeTier: string;
  displayName: string;
  cells: PayoutMatrixCell[];
}

interface PayoutMatrixData {
  operatorId: number;
  operatorName: string;
  gameName: string;
  availableOperators: Operator[];
  rows: PayoutMatrixRow[];
}

const PayoutMatrix: React.FC = () => {
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);

  const { data: payoutMatrix, isLoading, error } = useQuery<PayoutMatrixData>({
    queryKey: ['payout-matrix', selectedOperatorId],
    queryFn: async () => {
      const params = selectedOperatorId ? `?operatorId=${selectedOperatorId}` : '';
      const response = await axios.get(`/user/payout-matrix${params}`);
      return response.data;
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleOperatorChange = (operatorId: number) => {
    setSelectedOperatorId(operatorId);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load payout matrix. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!payoutMatrix) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No payout matrix data available.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const betTypes = ['Big', 'Small', '4A', 'A', 'ABC', '5D', '6D'];

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            {payoutMatrix.operatorName} - {payoutMatrix.gameName}
          </Typography>
          
          <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Select Operator:
            </Typography>
            {payoutMatrix.availableOperators.map((operator) => (
              <Button
                key={operator.id}
                variant={selectedOperatorId === operator.id || (!selectedOperatorId && operator.id === payoutMatrix.operatorId) ? "contained" : "outlined"}
                size="small"
                onClick={() => handleOperatorChange(operator.id)}
                sx={{
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {operator.name}
              </Button>
            ))}
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                  Details
                </TableCell>
                {betTypes.map((betType) => (
                  <TableCell 
                    key={betType}
                    sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', textAlign: 'center' }}
                  >
                    {betType}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutMatrix.rows.map((row) => (
                <TableRow key={row.prizeTier}>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    {row.displayName}
                  </TableCell>
                  {betTypes.map((betType) => {
                    const cell = row.cells.find(c => c.betType === betType);
                    return (
                      <TableCell 
                        key={betType}
                        sx={{ 
                          textAlign: 'center',
                          backgroundColor: cell?.isValid ? 'white' : '#f5f5f5'
                        }}
                      >
                        {cell && cell.isValid ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: cell.isCustom ? 'success.main' : 'text.secondary', 
                                fontSize: '0.7rem',
                                fontWeight: cell.isCustom ? 'bold' : 'normal'
                              }}
                            >
                              {cell.isCustom ? 'CUSTOM' : 'DEFAULT'}
                            </Typography>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: cell.isCustom ? 'success.main' : 'text.primary'
                              }}
                            >
                              {cell.multiplier?.toFixed(2) || '-'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Showing payout multipliers for {payoutMatrix.operatorName}.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PayoutMatrix;
